import React, { useState, useEffect, useCallback } from 'react';
import { useIsMobile } from '../lib/hooks';

interface FabContextValue {
  fabHandler: (() => void) | null;
  setFabHandler: (handler: (() => void) | null) => void;
  isRecording: boolean;
  setIsRecording: (v: boolean) => void;
}

const FabCtx = React.createContext<FabContextValue | null>(null);

export function useFab(): FabContextValue {
  const ctx = React.useContext(FabCtx);
  if (!ctx) throw new Error('useFab außerhalb Provider');
  return ctx;
}

export function FabProvider({ children }: { children: React.ReactNode }) {
  const isMobile = useIsMobile();
  const [_fabHandlerState, _setFabHandlerState] = useState<(() => void) | null>(null);
  const setFabHandler = useCallback((handler: (() => void) | null) => {
    _setFabHandlerState(handler !== null ? () => handler : null);
  }, []);
  const [_isRecording, _setIsRecording] = useState(false);
  const setIsRecording = useCallback((v: boolean) => _setIsRecording(v), []);

  // Keyboard-Shortcut Strg+D / Cmd+D (nur Desktop) — Welle K1: wie „Erzählen“ in der Seitenleiste
  // das allgemeine Diktat (Sidebar hört auf ld-diktat), nicht mehr das Modul-Diktat des Bildschirms
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'd' && !isMobile) {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('ld-diktat'));
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isMobile]);

  return (
    <FabCtx.Provider value={{ fabHandler: _fabHandlerState, setFabHandler, isRecording: _isRecording, setIsRecording }}>
      {children}
    </FabCtx.Provider>
  );
}
